import { logger } from "../../utils/logger";
import { createCorePipeline } from "../pipeline/core_pipeline_factory";
import { runDemoPipeline } from "../pipeline/pipeline";
import { buildExecutionReport } from "../report/report_builder";

export const buildCoreDemo = async ({
  user,
  demoConfig,
  seedProfile,
  industryTemplate,
  report,
  runId,
  onProgress,
} = {}) => {
  try {
    const steps = createCorePipeline({ demoConfig, seedProfile });
    const forwardProgress = (phase) => async ({
      index,
      total,
      step,
      result,
      error,
      sharedContext,
    }) => {
      await onProgress?.({
        phase,
        runId,
        current: index + 1,
        total,
        step: step?.id || step?.name,
        label: step?.label || step?.name,
        eventId: sharedContext?.eventResult?.eventId || null,
        result,
        error,
      });
    };

    const pipelineRun = await runDemoPipeline({
      context: { user, demoConfig, seedProfile, industryTemplate },
      steps,
      onStepStart: forwardProgress("running"),
      onStepComplete: forwardProgress("completed"),
      onStepFailure: forwardProgress("failed"),
    });

    const pipelineResults = pipelineRun.results;
    const demoContext = pipelineRun.demoContext;
    const executionReport = buildExecutionReport({ demoContext, pipelineResults });
    const successfulSteps = new Map(
      pipelineResults
        .filter((result) => result?.success)
        .map((result) => [result.step, result])
    );
    const addOnCreated = pipelineResults.reduce((created, result) => {
      if (!result?.success || typeof result.created !== "object") return created;
      return { ...created, ...result.created };
    }, {});

    const finalReport = {
      ...report,
      created: {
        ...report.created,
        event: successfulSteps.has("event_builder"),
        categories: demoContext?.categories?.length || 0,
        entries: demoContext?.entries?.length || 0,
        users: demoContext?.users?.length || 0,
        votes: demoContext?.votes?.length || 0,
        winnersReady: successfulSteps.has("vote_builder"),
        maps: successfulSteps.has("map_builder"),
        schedule: successfulSteps.has("schedule_builder"),
        seo: successfulSteps.has("seo_seeder"),
        homepage: successfulSteps.has("homepage_builder"),
      },
      addOns: { ...report.addOns, ...addOnCreated },
      links: {
        publicUrl: demoContext?.eventResult?.slug
          ? `/${demoContext.eventResult.slug}`
          : "",
        managerUrl: (demoContext?.eventResult?.slug || demoContext?.eventResult?.eventId)
          ? `/event/${demoContext.eventResult.slug || demoContext.eventResult.eventId}`
          : "",
      },
      messages: [
        ...(report.messages || []),
        "Core demo event created by the LGV Experience Engine pipeline.",
      ],
      executionReport,
      pipelineResults,
    };

    return {
      eventId: demoContext?.eventResult?.eventId || null,
      eventName: demoContext?.eventResult?.eventName || "Demo Event",
      eventSlug: demoContext?.eventResult?.slug || "",
      slug: demoContext?.eventResult?.slug || "",
      demoEngine: {
        config: demoConfig,
        context: demoContext,
        report: finalReport,
      },
    };
  } catch (error) {
    logger.warn("Experience Engine core builder failed", { value: error });
    throw error;
  }
};
